package ai.jessie.web.projects

import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.AlertListStyle
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.formSubmitButton
import ai.jessie.web.theme.slideOver
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// UC-010 slide-over (`docs/design/screens/13-PositionSlideOver.png`) around the unchanged flow: the
// project form with its positions (steps 4–10), then the review (step 11) inside the same panel.
// Closing cancels (A10); nothing is saved before confirmation.
@Composable
internal fun projectEditor(state: ProjectUiState, actions: ProjectActions) {
    val focusReturn = remember { FocusReturn().also { it.pending = true } }
    val reviewing = state.pending != null
    val signedIn = state.error?.authenticationRequired != true
    slideOver(
        panelId = PROJECT_EDITOR_ID,
        labelledBy = if (reviewing) PROJECT_REVIEW_TITLE_ID else PROJECT_TITLE_ID,
        close = actions.cancel,
        busy = state.busy,
        heading = {
            if (reviewing) projectReviewHeading(state)
            else {
                projectHeading(
                    if (state.current == null) "Projekt erfassen" else "Projekt bearbeiten",
                    state,
                    focusReturn,
                )
                P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
                    Text(
                        listOfNotNull(
                                state.current?.name,
                                "Positionen sind die Arbeitspakete, auf die Arbeit erfasst wird.",
                            )
                            .joinToString(" · ")
                    )
                }
            }
        },
        actions = {
            if (reviewing) {
                themedButton(
                    "Abbrechen",
                    actions.cancel,
                    variant = ButtonVariant.GHOST,
                    disabled = state.busy,
                )
                themedButton("Zurück zur Bearbeitung", actions.revise, disabled = state.busy)
                if (signedIn)
                    themedButton(
                        CONFIRM_LABEL,
                        actions.confirm,
                        variant = ButtonVariant.PRIMARY,
                        disabled = state.busy,
                    )
            } else {
                themedButton("Abbrechen", actions.cancel, disabled = state.busy)
                if (signedIn) formSubmitButton("Angaben prüfen", PROJECT_FORM_ID, state.busy)
            }
        },
    ) {
        state.error?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, state.busy) } }
        if (state.busy) projectStatus("Wird geladen oder gespeichert …")
        projectProblems(state.problems)
        if (signedIn) {
            if (reviewing) projectReview(state, focusReturn) else projectForm(state, actions)
        }
    }
}

@Composable
private fun projectHeading(title: String, state: ProjectUiState, focusReturn: FocusReturn) {
    H2(
        attrs = {
            id(PROJECT_TITLE_ID)
            attr("tabindex", "-1")
        }
    ) {
        Text(title)
    }
    LaunchedEffect(Unit) {
        // While a problem is shown, the alert or the marked field keeps focus.
        if (focusReturn.pending && state.error == null && !AppGlobals.isExporting)
            (document.getElementById(PROJECT_TITLE_ID) as? HTMLElement)?.focus()
        focusReturn.pending = true
    }
}

// A6, A7: every invalid field with its correction; marked fields refer to this list.
@Composable
private fun projectProblems(problems: Map<String, String>) {
    if (problems.isEmpty()) return
    Ul(attrs = AlertListStyle.toAttrs { id("project-problems") }) {
        problems.forEach { (field, correction) ->
            Li { Text("${projectFieldLabel(field)}: $correction") }
        }
    }
}

internal const val PROJECT_EDITOR_ID = "project-editor"
internal const val PROJECT_FORM_ID = "project-form"
internal const val CONFIRM_LABEL = "Bestätigen und speichern"
